import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useAuth } from '../context/AuthContext';

const WelcomeOverlay = () => {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [doNotShowAgain, setDoNotShowAgain] = useState(false);
    const BASE_STORAGE_KEY = 'deepalert_academy_welcome_shown';

    useEffect(() => {
        if (!user || !user.email) return;

        // Check if user has seen the welcome overlay before (using specific email key)
        const storageKey = `${BASE_STORAGE_KEY}_${user.email}`;
        const hasSeenWelcome = localStorage.getItem(storageKey);

        if (!hasSeenWelcome) {
            // Small delay for better UX
            setTimeout(() => setOpen(true), 500);
        }
    }, [user]);

    const handleClose = () => {
        setOpen(false);
        if (doNotShowAgain && user && user.email) {
            const storageKey = `${BASE_STORAGE_KEY}_${user.email}`;
            localStorage.setItem(storageKey, 'true');
        }
    };

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                }
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                }
            }}
        >
            <DialogTitle sx={{
                bgcolor: 'primary.main',
                color: 'white',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span role="img" aria-label="wave" style={{ fontSize: '1.5rem' }}>👋</span>
                    <Typography variant="h5" component="span" fontWeight="bold">
                        Welcome to the DeepAlert Training Academy
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Typography variant="body1" paragraph>
                    This is your learning hub for mastering the DeepAlert system.
                </Typography>

                <List sx={{ py: 0 }}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <DashboardIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle1" fontWeight="600">Dashboard</Typography>}
                            secondary="Browse available courses, track your progress, and view your certificates."
                        />
                    </ListItem>

                    <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <VerifiedUserIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle1" fontWeight="600">Verify Certificate</Typography>}
                            secondary="Check the authenticity of any DeepAlert course certificate."
                        />
                    </ListItem>

                    <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <AccountCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle1" fontWeight="600">Profile</Typography>}
                            secondary="Keep your name and details up to date for certificates."
                        />
                    </ListItem>
                </List>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    You can always return here from the main menu at any time.
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={doNotShowAgain}
                            onChange={(e) => setDoNotShowAgain(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Do not show this again"
                    sx={{ alignSelf: 'flex-start', ml: 0.5 }}
                />
                <Button
                    onClick={handleClose}
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        textTransform: 'none'
                    }}
                >
                    Got it — Let's Begin
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WelcomeOverlay;
